import { useCallback } from 'react';
import { useLanguageContext } from '../Context/LanguageContext';
import type { LocalizedString, LocalizedText, RoleDetail } from '../services/sanity/types';

type SupportedLanguage = 'en' | 'no';

/**
 * Hook for extracting localized content from Sanity fields based on current language
 */
export const useSanityLocalized = () => {
  const { currentLanguage } = useLanguageContext();
  const langCode = (currentLanguage?.code || 'no') as SupportedLanguage;

  /**
   * Get localized value from a LocalizedString field
   */
  const getLocalizedValue = useCallback(
    (field: LocalizedString | null | undefined, fallback = ''): string => {
      if (!field) return fallback;
      return field[langCode] || field.en || fallback;
    },
    [langCode]
  );

  /**
   * Get localized value from a LocalizedText field
   */
  const getLocalizedText = useCallback(
    (field: LocalizedText | null | undefined, fallback = ''): string => {
      if (!field) return fallback;
      return field[langCode] || field.en || fallback;
    },
    [langCode]
  );

  /**
   * Map array of LocalizedText items to array of strings in current language
   */
  const getLocalizedArray = useCallback(
    (arr: LocalizedText[] | null | undefined): string[] => {
      if (!arr || !Array.isArray(arr)) return [];
      return arr.map((item) => item[langCode] || item.en || '');
    },
    [langCode]
  );

  /**
   * Map array of LocalizedString items to array of strings in current language
   */
  const getLocalizedStringArray = useCallback(
    (arr: LocalizedString[] | null | undefined): string[] => {
      if (!arr || !Array.isArray(arr)) return [];
      return arr.map((item) => item[langCode] || item.en || '');
    },
    [langCode]
  );

  /**
   * Get localized values from RoleDetail array
   */
  const getLocalizedRoleDetails = useCallback(
    (details: RoleDetail[] | null | undefined): string[] => {
      if (!details || !Array.isArray(details)) return [];
      return details.map((detail) => detail[langCode] || detail.en || '');
    },
    [langCode]
  );

  /**
   * Join localized text array into paragraphs (for narrative content)
   */
  const getLocalizedParagraphs = useCallback(
    (arr: LocalizedText[] | null | undefined, separator = '\n\n'): string => {
      const texts = getLocalizedArray(arr);
      return texts.join(separator);
    },
    [getLocalizedArray]
  );

  return {
    langCode,
    getLocalizedValue,
    getLocalizedText,
    getLocalizedArray,
    getLocalizedStringArray,
    getLocalizedRoleDetails,
    getLocalizedParagraphs,
  };
};

export default useSanityLocalized;
